import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpLeft } from "lucide-react";
import Footer from "@/components/athena/Footer";

const SECTIONS = [
  {
    heading: "1. Who we are",
    body: (
      <>
        Athena Nutrascience Pvt. Ltd. (&ldquo;Athena&rdquo;, &ldquo;we&rdquo;,
        &ldquo;our&rdquo;) is a nutraceutical, biotechnology and life-sciences
        company registered in India (Mumbai 400&nbsp;063). This policy explains
        how we collect, use, store and protect the personal information you
        provide when you interact with our website
        <span className="whitespace-nowrap"> athenanutrascience.com</span>.
      </>
    ),
  },
  {
    heading: "2. Information we collect",
    body: (
      <>
        We collect only the information you voluntarily submit through our
        contact form:
        <ul className="mt-4 space-y-2 pl-5 list-disc marker:text-[#B89B64]">
          <li>Your name</li>
          <li>Your email address</li>
          <li>Your company or institution</li>
          <li>Subject line</li>
          <li>The message body you write to us</li>
        </ul>
        We do <strong>not</strong> collect payment information, health data or
        any sensitive personal category. We do not use behavioural tracking
        cookies, ad-tech pixels or third-party analytics on this site.
      </>
    ),
  },
  {
    heading: "3. How we use your information",
    body: (
      <>
        The information you submit is used solely to respond to your enquiry —
        typically within one business day — and to keep an internal record of
        our correspondence. We do <strong>not</strong> sell, rent, share or
        trade your information with any third party for marketing purposes.
      </>
    ),
  },
  {
    heading: "4. How we store & secure your information",
    body: (
      <>
        Contact-form submissions are stored in a managed MongoDB database with
        encryption at rest and TLS in transit. Notification emails are
        delivered through <span className="whitespace-nowrap">Resend</span>
        {" "}(our email service provider) over encrypted SMTP. Access to the
        database is restricted to authorised Athena personnel. We retain
        submissions for as long as reasonably needed to service your enquiry
        and to comply with applicable record-keeping obligations, and then
        delete them.
      </>
    ),
  },
  {
    heading: "5. Third-party service providers",
    body: (
      <>
        We use a small number of processors to operate our site:
        <ul className="mt-4 space-y-2 pl-5 list-disc marker:text-[#B89B64]">
          <li>
            <strong>Resend</strong> — transactional email delivery
            (submission notifications)
          </li>
          <li>
            <strong>MongoDB Atlas</strong> — encrypted document storage of
            contact submissions
          </li>
          <li>
            <strong>Emergent</strong> — hosting platform for the website
          </li>
        </ul>
        Each provider processes data only under our instruction and is bound
        by contractual data-protection commitments.
      </>
    ),
  },
  {
    heading: "6. Your rights",
    body: (
      <>
        You may at any time request access to, correction of, or deletion of
        the personal information you have shared with us. To exercise these
        rights, write to
        {" "}
        <a
          href="mailto:connect@athenanutrascience.com"
          className="link-underline text-[#1A382A] font-medium"
        >
          connect@athenanutrascience.com
        </a>{" "}
        with the email address you originally used, and we will respond within
        30 days.
      </>
    ),
  },
  {
    heading: "7. Cookies",
    body: (
      <>
        This website uses only strictly necessary technical cookies required
        for the page to function (e.g. session state). We do not use tracking,
        advertising or analytics cookies.
      </>
    ),
  },
  {
    heading: "8. Children",
    body: (
      <>
        Our site and services are intended for a professional B2B audience.
        We do not knowingly collect personal information from anyone under
        the age of 16.
      </>
    ),
  },
  {
    heading: "9. Changes to this policy",
    body: (
      <>
        We may update this policy from time to time to reflect changes in our
        practice or in applicable law. Material changes will be reflected on
        this page with a revised &ldquo;last updated&rdquo; date.
      </>
    ),
  },
  {
    heading: "10. Contact",
    body: (
      <>
        For any question about this policy or your personal data, please
        email{" "}
        <a
          href="mailto:connect@athenanutrascience.com"
          className="link-underline text-[#1A382A] font-medium"
        >
          connect@athenanutrascience.com
        </a>
        .
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy — Athena Nutrascience";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App bg-[#F8F7F4] min-h-screen">
      {/* Simple top bar */}
      <header className="border-b border-[#D5D1C8] bg-[#F8F7F4]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="privacy-nav-home"
            className="flex items-center gap-3 group"
          >
            <span className="w-8 h-8 rounded-full border border-[#1A382A] flex items-center justify-center">
              <span className="w-2 h-2 rounded-full bg-[#1A382A] group-hover:bg-[#B89B64] transition-colors" />
            </span>
            <span className="font-display text-2xl tracking-tight text-[#0A1713] leading-none">
              Athena<span className="text-[#B89B64]">.</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="flex items-center gap-2 label-mono hover:text-[#1A382A] transition-colors"
          >
            <ArrowUpLeft size={14} strokeWidth={1.5} /> BACK TO HOME
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-[1100px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-10">
        <span className="label-mono">// LEGAL · PRIVACY POLICY</span>
        <h1
          data-testid="privacy-title"
          className="font-display text-4xl sm:text-5xl md:text-6xl mt-6 leading-[1.02] tracking-tight text-[#0A1713]"
        >
          How we handle
          <br />
          your <em className="italic text-[#1A382A]">information</em>.
        </h1>
        <p className="mt-6 max-w-2xl text-[#3D4F46] leading-relaxed">
          We keep this simple: we collect only what you send us through our
          contact form, we use it only to reply to you, and we don&apos;t sell
          or share it. Below is the full detail.
        </p>
        <div className="mt-8 label-mono text-[#3D4F46]">
          Last updated: December 2025
        </div>
      </section>

      {/* Body */}
      <section className="max-w-[1100px] mx-auto px-6 md:px-10 pb-20 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-16">
          {/* Sticky mini-TOC */}
          <aside className="lg:col-span-1 lg:sticky lg:top-32 lg:self-start">
            <div className="label-mono mb-6">/ ON THIS PAGE</div>
            <ol className="space-y-2 text-sm">
              {SECTIONS.map((s) => {
                const id = s.heading.split(" ")[0].replace(".", "");
                return (
                  <li key={s.heading}>
                    <a
                      href={`#s${id}`}
                      className="text-[#3D4F46] hover:text-[#1A382A] transition-colors block"
                    >
                      {s.heading}
                    </a>
                  </li>
                );
              })}
            </ol>
          </aside>

          <article className="lg:col-span-2 space-y-12">
            {SECTIONS.map((s) => {
              const id = s.heading.split(" ")[0].replace(".", "");
              return (
                <div
                  key={s.heading}
                  id={`s${id}`}
                  className="scroll-mt-32 border-t border-[#D5D1C8] pt-8 first:border-t-0 first:pt-0"
                >
                  <h2 className="font-display text-2xl md:text-3xl text-[#0A1713] leading-tight">
                    {s.heading}
                  </h2>
                  <div className="mt-4 text-[#3D4F46] leading-relaxed text-base">
                    {s.body}
                  </div>
                </div>
              );
            })}
          </article>
        </div>
      </section>

      <Footer />
    </div>
  );
}
